import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Upload, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ref, uploadBytesResumable } from "firebase/storage";
import { storage } from "@/config/firebaseConfig";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDeleteAssessmentSession } from "@/hooks/mutations/use-sales-teams";
import { useTRPC } from "@/trpc/client";
import {
  CAPACIO_INSTRUMENTS,
  DEFAULT_ASSESSMENT_PROVIDER_ID,
  type CapacioInstrumentKey,
} from "@/intelligence/assessmentProviderCatalog";

const MAX_BYTES = 20 * 1024 * 1024;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId: string;
  memberId: string;
  memberName: string;
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function newResultFileId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

interface FileSlotProps {
  label: string;
  file: File | null;
  progress: number;
  uploading: boolean;
  onPick: (file: File | null) => void;
}

function FileSlot({ label, file, progress, uploading, onPick }: FileSlotProps) {
  const { t } = useTranslation("intelligence");
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") {
      toast({
        title: t("assessmentSession.toasts.onlyPdf"),
        description: t("assessmentSession.toasts.onlyPdfDesc"),
        variant: "destructive",
      });
      return;
    }
    if (f.size > MAX_BYTES) {
      toast({
        title: t("assessmentSession.toasts.tooLarge"),
        description: t("assessmentSession.toasts.sizeLimitDesc", {
          maxSize: Math.round(MAX_BYTES / 1024 / 1024),
        }),
        variant: "destructive",
      });
      return;
    }
    onPick(f);
  };

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        onChange={handlePick}
        className="hidden"
      />
      {file ? (
        <div className="flex items-center justify-between rounded-md border p-3 text-sm">
          <div className="min-w-0 flex-1 truncate">
            <div className="truncate font-medium">{file.name}</div>
            <div className="text-xs text-muted-foreground">
              {(file.size / 1024 / 1024).toFixed(2)} MB
            </div>
            {uploading && progress > 0 && (
              <Progress value={progress} className="mt-2 h-1" />
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            disabled={uploading}
            onClick={() => onPick(null)}
            aria-label={t("assessmentSession.fileSlot.removeBtnLabel")}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="flex w-full flex-col items-center justify-center rounded-md border-2 border-dashed p-5 text-sm text-muted-foreground transition-colors hover:bg-accent disabled:opacity-50"
        >
          <Upload className="mb-1.5 h-5 w-5" />
          {t("assessmentSession.fileSlot.selectPdf")}
        </button>
      )}
    </div>
  );
}

export function AssessmentSessionDialog({
  open,
  onOpenChange,
  businessId,
  memberId,
  memberName,
}: Props) {
  const { t } = useTranslation("intelligence");
  const { toast } = useToast();
  const trpc = useTRPC();
  const qc = useQueryClient();
  // No onSuccess invalidation here — queries are invalidated only after all
  // Storage uploads complete so lists never show a session before its PDFs
  // are actually in Storage.
  const createSession = useMutation(trpc.salesTeams.createSession.mutationOptions());
  const deleteSession = useDeleteAssessmentSession();

  const [filesByInstrument, setFilesByInstrument] = useState<
    Partial<Record<CapacioInstrumentKey, File | null>>
  >({});
  const [progressByInstrument, setProgressByInstrument] = useState<
    Partial<Record<CapacioInstrumentKey, number>>
  >({});
  const [label, setLabel] = useState("");
  const [takenAt, setTakenAt] = useState(todayIsoDate());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setFilesByInstrument({});
      setProgressByInstrument({});
      setLabel("");
      setTakenAt(todayIsoDate());
      setBusy(false);
    }
  }, [open]);

  const canSave = useMemo(() => {
    const hasAny = CAPACIO_INSTRUMENTS.some(
      (i) => Boolean(filesByInstrument[i.instrumentKey]),
    );
    return hasAny && !busy;
  }, [filesByInstrument, busy]);

  const uploadOne = (
    sessionId: string,
    resultFileId: string,
    file: File,
    instrumentKey: CapacioInstrumentKey,
  ): Promise<{
    storagePath: string;
    fileName: string;
    contentType: string;
    sizeBytes: number;
  }> => {
    const path = `Businesses/${businessId}/salesTeamsMembers/${memberId}/assessmentSessions/${sessionId}/resultFiles/${resultFileId}.pdf`;
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file, {
      contentType: file.type || "application/pdf",
    });
    return new Promise((resolve, reject) => {
      task.on(
        "state_changed",
        (snap) => {
          const pct = Math.round(
            (snap.bytesTransferred / snap.totalBytes) * 100,
          );
          setProgressByInstrument((p) => ({ ...p, [instrumentKey]: pct }));
        },
        (err) => reject(err),
        () =>
          resolve({
            storagePath: path,
            fileName: file.name,
            contentType: file.type || "application/pdf",
            sizeBytes: file.size,
          }),
      );
    });
  };

  const handleSave = async () => {
    if (!canSave) return;
    setBusy(true);
    setProgressByInstrument({});

    const sessionId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    // Create the session in Firestore BEFORE uploading: the storage trigger
    // that parses each PDF (score + AI summary) looks up the session doc and
    // its resultFiles entry when the object finalizes — if the doc doesn't
    // exist yet, parsing is silently skipped and the results never appear.
    const pending = CAPACIO_INSTRUMENTS.filter(
      (inst) => filesByInstrument[inst.instrumentKey],
    ).map((inst) => {
      const file = filesByInstrument[inst.instrumentKey]!;
      const rid = newResultFileId();
      return { rid, instrumentKey: inst.instrumentKey, file };
    });

    const resultFiles: Record<
      string,
      {
        instrumentKey: CapacioInstrumentKey;
        file: {
          storagePath: string;
          fileName: string;
          contentType: string;
          sizeBytes: number;
        };
      }
    > = {};
    for (const { rid, instrumentKey, file } of pending) {
      resultFiles[rid] = {
        instrumentKey,
        file: {
          storagePath: `Businesses/${businessId}/salesTeamsMembers/${memberId}/assessmentSessions/${sessionId}/resultFiles/${rid}.pdf`,
          fileName: file.name,
          contentType: file.type || "application/pdf",
          sizeBytes: file.size,
        },
      };
    }

    try {
      await createSession.mutateAsync({
        businessId,
        memberId,
        sessionId,
        label: label.trim() || null,
        takenAt: takenAt ? new Date(takenAt).toISOString() : null,
        providerId: DEFAULT_ASSESSMENT_PROVIDER_ID,
        resultFiles,
      });
    } catch (err) {
      console.error("Session create failed:", err);
      toast({
        title: t("assessmentSession.toasts.error"),
        variant: "destructive",
      });
      setBusy(false);
      return;
    }

    try {
      await Promise.all(
        pending.map(({ rid, instrumentKey, file }) =>
          uploadOne(sessionId, rid, file, instrumentKey),
        ),
      );

      // Invalidate only after all PDFs are in Storage so lists never show a
      // session entry pointing at missing objects.
      qc.invalidateQueries({ queryKey: [["salesTeams"]] });
      toast({
        title: t("assessmentSession.toasts.created"),
      });
      onOpenChange(false);
    } catch (err) {
      console.error("Session upload failed:", err);
      // Await the rollback before clearing busy so the dialog stays disabled
      // while the Firestore delete is in flight — prevents a retry from racing
      // the rollback and creating a duplicate/conflicting session.
      await deleteSession
        .mutateAsync({ businessId, memberId, sessionId })
        .catch(() => undefined);
      toast({
        title: t("assessmentSession.toasts.error"),
        variant: "destructive",
      });
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("assessmentSession.title")}</DialogTitle>
          <DialogDescription>
            {t("assessmentSession.description", { memberName })}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          {CAPACIO_INSTRUMENTS.map((inst) => (
            <FileSlot
              key={inst.instrumentKey}
              label={inst.title}
              file={filesByInstrument[inst.instrumentKey] ?? null}
              progress={progressByInstrument[inst.instrumentKey] ?? 0}
              uploading={busy}
              onPick={(f) =>
                setFilesByInstrument((prev) => ({
                  ...prev,
                  [inst.instrumentKey]: f,
                }))
              }
            />
          ))}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="session-date">{t("assessmentSession.date")}</Label>
            <Input
              id="session-date"
              type="date"
              value={takenAt}
              onChange={(e) => setTakenAt(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="session-label">{t("assessmentSession.label")}</Label>
            <Input
              id="session-label"
              type="text"
              placeholder={t("assessmentSession.labelPlaceholder")}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={160}
              disabled={busy}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            {t("assessmentSession.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("assessmentSession.upload")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
